"use client";
import { TIndustriesConfig } from "@/app/config";
import { Play, Music, Gamepad2, Users, Headphones, Calendar } from "lucide-react";
import React from "react";
import { motion } from "motion/react";

const contentIcons = [
  {
    icon: Play,
  },
  {
    icon: Music,
  },
  {
    icon: Gamepad2,
  },
  {
    icon: Users,
  },
  {
    icon: Headphones,
  },
  {
    icon: Calendar,
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};


export default function ContentConsumptionPatterns({
  config,
}: {
  config: TIndustriesConfig["contentConsumptionPatterns"];
}) {
  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: "easeOut",
              },
            },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Content Cards Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {config.contentTypes.map((content, index) => {
            const Icons = contentIcons[index].icon;
            return (
              <motion.div
                key={index}
                className={`${content.bgColor} rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300 relative overflow-hidden`}
                variants={{
                  hidden: { 
                    opacity: 0, 
                    y: 50,
                    scale: 0.95,
                  },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                    },
                  },
                  hover: {
                    y: -8,
                    scale: 1.02,
                    transition: {
                      duration: 0.2,
                      ease: "easeOut",
                    },
                  },
                }}
                whileHover="hover"
                layout
              >
                {/* Background decoration */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Badge */}
                <motion.div 
                  className="absolute top-4 right-4"
                  variants={ {
                    hidden: { opacity: 0, scale: 0 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: 0.2,
                      },
                    },
                  }}
                >
                  <motion.span
                    className={`${content.badgeColor} text-white text-xs font-semibold px-2 py-1 rounded-full`}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    {content.badge}
                  </motion.span>
                </motion.div>

                {/* Icon and Title */}
                <div className="flex items-start gap-3 mb-4">
                  <motion.div
                    className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
                    variants={ {
                      hidden: { scale: 0, rotate: -180 },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        transition: {
                          type: "spring",
                          stiffness: 200,
                          damping: 15,
                          delay: 0.1,
                        },
                      },
                      hover: {
                        rotate: 360,
                        scale: 1.1,
                        transition: {
                          duration: 0.3,
                        },
                      },
                    }}
                    whileHover="hover"
                  >
                    <Icons className={`w-5 h-5 ${content.iconColor}`} />
                  </motion.div>
                  <motion.h3
                    className="text-lg font-semibold text-gray-900 leading-tight pt-1"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    {content.title}
                  </motion.h3>
                </div>

                {/* Audience Info */}
                <motion.p
                  className="text-gray-600 text-sm mb-4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {content.audience}
                </motion.p>

                {/* Metrics */}
                <motion.div 
                  className="space-y-3"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ staggerChildren: 0.1 }}
                >
                  {/* Average Time */}
                  <motion.div
                    className="flex justify-between items-center"
                    variants={ {
                      hidden: { opacity: 0, x: -20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.4,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <span className="text-gray-700 font-medium text-sm">
                      Average Time
                    </span>
                    <motion.span
                      className="text-gray-900 font-bold"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      {content.avgTime}
                    </motion.span>
                  </motion.div>

                  {/* Growth Rate */}
                  <motion.div
                    className="flex justify-between items-center"
                    variants={ {
                      hidden: { opacity: 0, x: -20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.4,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    <span className="text-gray-700 font-medium text-sm">
                      {content.growthLabel}
                    </span>
                    <motion.span
                      className={`font-bold ${
                        content.growthRate.startsWith("+")
                          ? "text-green-600"
                          : content.growthRate.startsWith("-")
                          ? "text-red-600"
                          : "text-gray-900"
                      }`}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      {content.growthRate}
                    </motion.span>
                  </motion.div>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}